import * as React from "react";
import {useCallback, useEffect, useRef, useState} from "react";
import DnDGridElement from "../../Common/DnDGridElement";
import {makeAccessor, makeHeader, prepareSortQuery} from "../../Utils/gridUtils";
import {Close, Edit, Visibility} from "@mui/icons-material";
import {
    Box,
    Button,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    IconButton as MuiIconButton,
    Tooltip,
    Typography
} from "@mui/material";
import ManageFoodOrderModal from "./ManageFoodOrderModal";
import EditTable from '../../Common/EditTable';
import {getFoodOrders, previewEmail} from "../../../API/foodOrders";
import {toast} from "react-toastify";
import {format, getWeek, getYear} from "date-fns";
import FilterControls from "./FilterControls";
import {fetchPut} from "../../../API/util";
import {formatTime} from "../../Utils/dateUtils";

const FoodOrdersGrid = () => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [rowsCount, setRowsCount] = useState(0); // Total records
    const [totalPage, setTotalPage] = useState(0); // Total pages
    const [filters, setFilters] = useState([]);
    const [controlledPageIndex, setControlledPageIndex] = useState(0);
    const [refreshGridData, setRefreshGridData] = useState(false);
    const [openManageModal, setOpenManageModal] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [viewArchived] = useState(false);
    const [openPreviewModal, setOpenPreviewModal] = useState(false);
    const [previewContent, setPreviewContent] = useState("");
    const viewArchivedRef = useRef(viewArchived);
    const [isOpen, setIsOpen] = useState(false);
    const handleClose = () => setIsOpen(false);

    // State for filters
    const [countryRegion, setCountryRegion] = useState("");
    const [emailStatus, setEmailStatus] = useState("");
    const [selectedWeekDate, setSelectedWeekDate] = useState(getWeek(new Date(), {weekStartsOn: 1}).toString());
    const [selectedDate, setSelectedDate] = useState(null);
    const [selectedMonth, setSelectedMonth] = useState("");
    const [selectedYear, setSelectedYear] = useState(getYear(new Date()).toString());
    const [city, setCity] = useState("");
    const [resetLoading, setResetLoading] = useState(false);

    useEffect(() => {
        viewArchivedRef.current = viewArchived;
    }, [viewArchived]);

    // Define default columns with searchability settings
    const defaultCols = [
        {
            id: "food_order_id",
            column: "food_order_id",
            accessor: makeAccessor("food_order_id"),
            Header: makeHeader("Order ID"),
            minWidth: 100,
            disableFilters: true,
        },
        {
            id: "week_number",
            column: "week_number",
            accessor: makeAccessor("week_number"),
            Header: makeHeader("Week Number"),
            minWidth: 100,
            disableFilters: true, // Not searchable
        },
        {
            id: "product_group",
            column: "product_group",
            accessor: makeAccessor("product_group"),
            Header: makeHeader("Product Group / Abbr"),
            minWidth: 150,
            disableSortBy: true,
            filter: "fuzzyText", // Searchable
        },
        {
            id: "event_start",
            column: "event_start",
            accessor: makeAccessor("event_start"),
            Header: makeHeader("Event Start"),
            minWidth: 100,
            disableFilters: true, // Not searchable
        },
        {
            id: "delivery_time",
            column: "delivery_time",
            accessor: makeAccessor("delivery_time"),
            Header: makeHeader("Delivery Time"),
            minWidth: 150,
            disableFilters: true, // Not searchable
        },
        {
            id: "num_orders",
            column: "num_orders",
            accessor: makeAccessor("num_orders"),
            Header: makeHeader("# of Orders"),
            minWidth: 100,
            disableSortBy: true,
            disableFilters: true, // Not searchable
        },
        {
            id: "sandwich_amount",
            column: "sandwich_amount",
            accessor: makeAccessor("sandwich_amount"),
            Header: makeHeader("Sandwiches (CPH)"),
            minWidth: 150,
            disableSortBy: true,
            disableFilters: true, // Not searchable
        },
        {
            id: "fika",
            column: "fika",
            accessor: makeAccessor("fika"),
            Header: makeHeader("FIKA (SE/STO)"),
            minWidth: 130,
            disableFilters: true, // Not searchable
        },
        {
            id: "members_attending",
            column: "members_attending",
            accessor: makeAccessor("members_attending"),
            Header: makeHeader("Members (Attending)"),
            minWidth: 180,
            disableSortBy: true,
            disableFilters: true, // Not searchable
        },
        {
            id: "members_tentative",
            column: "members_tentative",
            accessor: makeAccessor("members_tentative"),
            Header: makeHeader("Members (Tentative)"),
            minWidth: 180,
            disableSortBy: true,
            disableFilters: true, // Not searchable
        },
        {
            id: "nd_count",
            column: "nd_count",
            accessor: makeAccessor("nd_count"),
            Header: makeHeader("ND Count"),
            minWidth: 100,
            disableSortBy: true,
            disableFilters: true, // Not searchable
        },
        {
            id: "speaker_count",
            column: "speaker_count",
            accessor: makeAccessor("speaker_count"),
            Header: makeHeader("Speakers"),
            disableSortBy: true,
            disableFilters: true, // Not searchable
        },
        {
            id: "defacto",
            column: "defacto",
            accessor: makeAccessor("defacto"),
            Header: makeHeader("Defacto"),
            disableSortBy: true,
            disableFilters: true, // Not searchable
        },
        {
            id: "difference",
            column: "difference",
            accessor: makeAccessor("difference"),
            Header: makeHeader("Difference"),
            disableSortBy: true,
            disableFilters: true, // Not searchable
        },
        {
            id: "comments",
            column: "comments",
            accessor: makeAccessor("comments"),
            Header: makeHeader("Important/Comments"),
            filter: "fuzzyText", // Searchable
            Cell: ({row}) => (
                <div>
                    {row.original.comments.length > 0 && <div dangerouslySetInnerHTML={{
                        __html: row.original.comments,
                    }}></div>}
                </div>
            )
        },
        {
            id: "caterer_info",
            column: "caterer_info",
            accessor: makeAccessor("caterer_info"),
            Header: makeHeader("Caterer Info"),
            filter: "fuzzyText", // Searchable
        },
        {
            id: "pre_order_email_status",
            column: "pre_order_email_status",
            accessor: makeAccessor("pre_order_email_status"),
            Header: makeHeader("Pre Order Email Status"),
            filter: "fuzzyText", // Searchable
        },
        {
            id: "final_order_email_status",
            column: "final_order_email_status",
            accessor: makeAccessor("final_order_email_status"),
            Header: makeHeader("Final Order Email Status"),
            filter: "fuzzyText", // Searchable
        },
        {
            id: "pre_order_email_trigger_date",
            column: "pre_order_email_trigger_date",
            accessor: makeAccessor("pre_order_email_trigger_date"),
            Header: makeHeader("Pre Order Trigger Date"),
            disableFilters: true, // Not searchable
        },
        {
            id: "final_order_email_trigger_date",
            column: "final_order_email_trigger_date",
            accessor: makeAccessor("final_order_email_trigger_date"),
            Header: makeHeader("Final Order Trigger Date"),
            disableFilters: true, // Not searchable
        },
        {
            id: "pre_order_sent_at",
            column: "pre_order_sent_at",
            accessor: makeAccessor("pre_order_sent_at"),
            Header: makeHeader("Pre Order Send At"),
            disableFilters: true, // Not searchable
        },
        {
            id: "final_order_sent_at",
            column: "final_order_sent_at",
            accessor: makeAccessor("final_order_sent_at"),
            Header: makeHeader("Final Order Send At"),
            disableFilters: true, // Not searchable
        },
        {
            id: "end_time",
            column: "end_time",
            accessor: makeAccessor("end_time"),
            Header: makeHeader("End Time"),
            disableFilters: true, // Not searchable
        },
        {
            id: "location",
            column: "location",
            accessor: makeAccessor("location"),
            Header: makeHeader("Location"),
            disableSortBy: true,
            filter: "fuzzyText", // Searchable
        },
        {
            id: "Actions",
            Header: "Actions",
            Cell: ({row}) => (
                <div>
                    {row.original.final_order_email_status !== "Sent" ? (
                        <Tooltip title="Edit" arrow placement="top">
                            <IconButton
                                onClick={() => {
                                    setOpenManageModal(true);
                                    setSelectedOrder(row.original);
                                }}
                            >
                                <Edit color="primary"/>
                            </IconButton>
                        </Tooltip>
                    ) : (
                        <Tooltip title="Preview Email" arrow placement="top">
                            <IconButton
                                onClick={() => {
                                    handlePreviewEmail(row.original);
                                    setOpenPreviewModal(true);
                                }}
                            >
                                <Visibility sx={{color: "gray"}}/>
                            </IconButton>
                        </Tooltip>
                    )}
                </div>
            ),
            disableFilters: true, // Actions column is not searchable
        },
    ];

    // Define searchable columns
    const searchableColumns = [
        "product_group",
        "comments",
        "caterer_info",
        "pre_order_email_status",
        "final_order_email_status",
        "location",
    ];

    // Initialize columns from localStorage with proper filter settings
    const [cols, setCols] = React.useState(() => {
        // Retrieve user settings from localStorage
        const userSettings = JSON.parse(localStorage.getItem("user"));
        const savedColumns = userSettings?.tableSettings?.food_orders?.columns || [];

        // If no saved columns, use defaultCols
        if (!savedColumns.length) {
            return defaultCols;
        }

        // Map saved columns to include proper settings from defaultCols
        const mergedCols = savedColumns.map((key) => {
            const defaultCol = defaultCols.find((col) => col.column === key || col.Header === makeHeader(key));
            if (defaultCol) {
                return {
                    ...defaultCol,
                    minWidth: defaultCol.minWidth || 150,
                    filter: searchableColumns.includes(key) ? "fuzzyText" : undefined,
                    disableFilters: !searchableColumns.includes(key),
                };
            }
            // Fallback for columns not in defaultCols (shouldn't happen)
            return {
                column: key,
                accessor: makeAccessor(key),
                Header: makeHeader(key),
                minWidth: 150,
                filter: searchableColumns.includes(key) ? "fuzzyText" : undefined,
                disableFilters: !searchableColumns.includes(key),
            };
        });

        // Ensure Actions column is included if it was in defaultCols
        const actionsCol = defaultCols.find((col) => col.Header === "Actions");
        if (actionsCol && !mergedCols.some((col) => col.Header === "Actions")) {
            mergedCols.push(actionsCol);
        }

        return mergedCols;
    });

    const filtersRef = useRef({
        countryRegion,
        emailStatus,
        selectedWeekDate,
        selectedDate,
        selectedMonth,
        selectedYear,
        city
    });

    useEffect(() => {
        filtersRef.current = {
            countryRegion,
            emailStatus,
            selectedWeekDate,
            selectedDate,
            selectedMonth,
            selectedYear,
            city,
        };
    }, [countryRegion, emailStatus, selectedWeekDate, selectedDate, selectedMonth, selectedYear, city]);

    const resetFilters = useCallback(() => {
        setResetLoading(true);
        setFilters([]);
        setCountryRegion("");
        setEmailStatus("");
        setSelectedWeekDate('all');
        setSelectedDate(null);
        setSelectedMonth("");
        setSelectedYear(getYear(new Date()).toString());
        setCity("");
        setRefreshGridData(true);
    }, []);

    useEffect(() => {
        if (!refreshGridData && resetLoading) {
            setResetLoading(false);
        }
    }, [refreshGridData, resetLoading]);

    const prepareCustomFilterQuery = (filters, _, customFilters) => {
        const query = {};

        const {
            countryRegion,
            emailStatus,
            selectedDate,
            selectedMonth,
            selectedYear,
            selectedWeekDate,
            city
        } = customFilters;

        if (countryRegion) {
            query.country = countryRegion === "Denmark" ? "DK" : countryRegion === "Sweden" ? "SE" : "";
        }
        if (emailStatus) query.email_status = emailStatus;
        if (selectedDate) query.date = format(selectedDate, "yyyy-MM-dd");
        if (selectedMonth) query.month = selectedMonth;
        if (selectedYear) query.year = selectedYear;
        if (selectedWeekDate) {
            query.week_number = selectedWeekDate === 'all' ? '' : selectedWeekDate;
        }

        if (city) query.city = city;

        filters.forEach((filter) => {
            if (filter.id && filter.value) {
                const field = filter.id.match(/contains\[(\w+)\]/)?.[1] || filter.id;
                if (!query[field]) {
                    query[field] = filter.value;
                }
            }
        });

        return query;
    };

    const fetchData = useCallback(
        ({pageSize, pageIndex, sortBy, filters, columns}) => {
            const query = prepareCustomFilterQuery(filters, columns, filtersRef.current);
            const sort = prepareSortQuery(sortBy, columns);

            setLoading(true);

            getFoodOrders(pageSize, pageIndex, query, sort)
                .then((res) => {
                    const filtered = res.data.map((order) => {
                        const isSingleOrder =
                            (order.has_child === null ||
                                order.has_child === 0) && order.parent_food_order_id == null;
                        const isParentOrder = order.has_child == 1;
                        const isChildOrder = order.parent_food_order_id != null

                        return {
                            ...order,
                            isSingleOrder: isSingleOrder,
                            isParentOrder: isParentOrder,
                            isChildOrder: isChildOrder,
                            event_start: order.event?.event_start ? format(new Date(order.event.event_start), "dd/MM/yyyy HH:mm") : "",
                            week_number: order.week_number,
                            product_group: order.order_config?.group?.abbr || "",
                            date: order.event?.event_start ? format(new Date(order.event.event_start), "dd/MM/yyyy") : "",
                            delivery_time: formatTime(order.delivery_time),
                            num_orders: order.order_config?.stats?.ORDER_AMOUNT || order.order_config?.order_amount || 0,
                            sandwich_amount:
                                order.order_config?.location?.country === "DK"
                                    ? order.order_config?.stats?.ORDER_AMOUNT || order.order_config?.order_amount || 0
                                    : 0,
                            fika: order.order_config?.location?.code && order.order_config.location.code.startsWith("SE/")
                                ? (order.fika && order.fika_time ? formatTime(order.fika_time) : (order.fika ? "Yes" : "No"))
                                : "N/A",
                            members_attending: order.order_config?.stats?.M_A || 0,
                            members_tentative: order.order_config?.stats?.M_T || 0,
                            nd_count: order.order_config?.stats?.N_D || 0,
                            speaker_count: order.order_config?.stats?.M_S || 0,
                            defacto: order.order_config?.stats?.DEFACTO || 0,
                            difference: order.order_config?.stats?.DIFF || 0,
                            comments: order.comments || "",
                            caterer_info: order?.caterer ? `${order.caterer.contact_person_name}` : "",
                            final_order_email_status: order.final_order_email_status || "",
                            pre_order_email_status: order.pre_order_email_status || "",
                            pre_order_email_trigger_date:
                                (order?.pre_order_email_trigger_date && order?.pre_order_email_trigger_date != null)
                                    ? format(
                                        new Date(order.pre_order_email_trigger_date.replace(" ", "T")),
                                        "dd/MM/yyyy HH:mm"
                                    )
                                    : "",
                            final_order_email_trigger_date:
                                (order.final_order_email_trigger_date && order?.final_order_email_trigger_date != null)
                                    ? format(
                                        new Date(order.final_order_email_trigger_date.replace(" ", "T")),
                                        "dd/MM/yyyy HH:mm"
                                    )
                                    : "",
                            pre_order_sent_at:
                                (order?.pre_order_sent_at && order?.pre_order_sent_at != null)
                                    ? format(
                                        new Date(order.pre_order_sent_at.replace(" ", "T")),
                                        "dd/MM/yyyy HH:mm"
                                    )
                                    : "",
                            final_order_sent_at:
                                (order?.final_order_sent_at && order?.final_order_sent_at != null)
                                    ? format(
                                        new Date(order.final_order_sent_at.replace(" ", "T")),
                                        "dd/MM/yyyy HH:mm"
                                    )
                                    : "",
                            end_time: formatTime(order.end_time),
                            location: order.order_config?.location
                                ? `${order.order_config.location.address}, ${order.order_config.location.city}`
                                : "",
                        };
                    });
                    setData(filtered);
                    setRowsCount(res.total);
                    setTotalPage(Math.ceil(res.total / res.per)); // Calculate total pages
                })
                .catch((error) => {
                    toast.error(error.error || error.message || 'Failed to fetch food orders');
                })
                .finally(() => setLoading(false));

            setRefreshGridData(false);
        },
        [countryRegion, emailStatus, selectedWeekDate, selectedDate, selectedMonth, selectedYear, city]
    );

    useEffect(() => {
        setControlledPageIndex(0);
        setRefreshGridData(true);
    }, [countryRegion, emailStatus, selectedWeekDate, selectedDate, selectedMonth, selectedYear, city, cols]);

    const handleManageClose = (refreshGrid = false) => {
        setOpenManageModal(false);
        setRefreshGridData(refreshGrid);
        setSelectedOrder(null);
    };

    const handlePreviewEmail = (values) => {
        setPreviewContent("");

        previewEmail(values)
            .then((res) => setPreviewContent(res.html))
            .catch((err) => {
                toast.error("Failed to preview email");
            });
    };

    const saveColumnOrder = async (columnOrder) => {
        try {
            const _user = JSON.parse(localStorage.getItem("user"));
            const columnOrders = columnOrder?.filter((item) => item);

            _user.tableSettings['food_orders'] = {
                columns: columnOrders,
                filters: _user.tableSettings['food_orders']?.filters ?? {sort: "created_at"},
                searchParameters: _user.tableSettings['food_orders']?.searchParameters ?? {
                    text: "",
                    searchBycolumn: ""
                }
            };

            localStorage.setItem("user", JSON.stringify(_user));
            await fetchPut(`/users/settings`, _user);
        } catch (error) {
            throw error;
        }
    };

    return (
        <>
            <div style={{display: "flex", justifyContent: "space-between", alignItems: "center"}}>
                <h2 className="page-title">Food Orders Overview</h2>
                <Button variant="outlined" color='primary' onClick={() => setIsOpen(true)}>Edit Table</Button>
            </div>

            <FilterControls
                countryRegion={countryRegion}
                setCountryRegion={setCountryRegion}
                emailStatus={emailStatus}
                setEmailStatus={setEmailStatus}
                selectedWeekDate={selectedWeekDate}
                setSelectedWeekDate={setSelectedWeekDate}
                selectedDate={selectedDate}
                setSelectedDate={setSelectedDate}
                selectedMonth={selectedMonth}
                setSelectedMonth={setSelectedMonth}
                selectedYear={selectedYear}
                setSelectedYear={setSelectedYear}
                city={city}
                setCity={setCity}
                resetFilters={resetFilters}
                setRefreshGridData={setRefreshGridData}
                resetLoading={resetLoading}
            />

            <DnDGridElement
                columns={cols}
                data={data}
                fetchData={fetchData}
                loading={loading}
                pageCount={totalPage}
                controlledPageIndex={controlledPageIndex}
                setControlledPageIndex={setControlledPageIndex}
                filters={filters}
                setFilters={setFilters}
                resetFilters={resetFilters}
                rowsCount={rowsCount}
                handleRowClick={() => {
                }}
                initialSortBy="event_start"
                sortOrder="desc"
                hideSelection={true}
                refreshGridData={refreshGridData}
                manual
                saveColumnOrder={saveColumnOrder}
            />

            <ManageFoodOrderModal
                open={openManageModal}
                handleClose={handleManageClose}
                data={selectedOrder}
            />

            <Dialog
                open={openPreviewModal}
                onClose={() => setOpenPreviewModal(false)}
                scroll="paper"
                fullWidth
                maxWidth="md"
                sx={{"& .MuiDialog-paper": {maxHeight: "80vh"}}}
            >
                <DialogTitle sx={{backgroundColor: "#1565c0", color: "white"}}>
                    Email Preview
                    <MuiIconButton
                        aria-label="close"
                        onClick={() => setOpenPreviewModal(false)}
                        sx={{
                            position: "absolute",
                            right: 8,
                            top: 8,
                            color: (theme) => theme.palette.grey[500],
                        }}
                    >
                        <Close/>
                    </MuiIconButton>
                </DialogTitle>
                <DialogContent sx={{padding: "20px", backgroundColor: "#F7F7F7"}}>
                    <Box sx={{
                        mt: 2,
                        '& table': {
                            borderCollapse: 'collapse !important',
                            width: '100% !important',
                            border: '1px solid #000 !important'
                        },
                        '& th, & td': {
                            border: '1px solid #000 !important',
                            padding: '6px !important'
                        },
                        '& th': {
                            fontWeight: 'bold !important',
                            color: '#000 !important'
                        }
                    }}>
                        {previewContent ? (
                            <div dangerouslySetInnerHTML={{__html: previewContent}}/>
                        ) : (
                            <Typography>No preview content available</Typography>
                        )}
                    </Box>
                </DialogContent>
            </Dialog>

            <EditTable
                open={isOpen}
                onClose={handleClose}
                columns={["food_orders"]}
                table="food_orders"
                setColumns={setCols}
            />
        </>
    );
};

export default FoodOrdersGrid;